import "./style.scss";

import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, InputLabel } from "@mui/material";
import CompanyPreferenceSettings from "components/CompanyPreferenceSettings";
import BlockModal from "components/shared/BlockModal";
import InfoBlockProfil from "components/shared/InfoBlockProfil";
import PaymentMethod from "components/shared/PaymentMethod";
import SaveButton from "components/shared/SaveButton";
import SocialProfile from "components/shared/SocialProfile";
import Upload from "components/shared/Upload";
import StockSettings from "components/StockSettings";
import { fileWeightValidator } from "helpers/importData-helpers";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker } from "react-router-dom";
import { toast } from "react-toastify";
import type { ICompany } from "store/settingsStore";
import { fetchCompanyInfo, sendCompanySignature } from "store/settingsStore";
import { postLocations, putLocations } from "store/stockStore";
import type { AppDispatch, RootState } from "store/store";
import { sendPicture } from "store/titleStore";

import { getCreateCompanySiretField } from "components/AdminPanel/TabPanels/createCompanySiret";

import { patchCompanyInfo } from "./../../store/settingsStore";
import { validateCompanyInfoBeforeSubmit } from "./utils";

export const labelStyles = {
    color: "#737373",
    width: "100%",
    justifyContent: "space-between",
    fontSize: "12px",
    lineHeight: "18px",
    ".MuiTypography-root": {
        fontSize: "12px",
    },
    "&.MuiInputLabel-root": {
        fontFamily: "Poppins",
        fontStyle: "normal",
        width: "200px",
    },
};

// TODO: use react-hook-form here
const CompanyInfo = (props: any) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const companySettings = useSelector((state: RootState) => state.settings.companySettings) as ICompany;
    const [companyData, setCompanyData] = useState(companySettings);
    const [isCompanyEdited, setIsCompanyEdited] = useState(false);
    const [isBankEdited, setIsBankEdited] = useState(false);
    const [isStockLocationsEdited, setIsStockLocationsEdited] = useState(false);
    const [companyInfo, setCompanyInfo] = useState({});
    const [address, setAddress] = useState({});
    const [bankInfo, setBankInfo] = useState({});
    const [additionalInfo, setAdditionalInfo] = useState({});
    const [locationList, setLocationList] = useState<string[]>([]);
    const [updateTypes, setUpdateTypes] = useState<Array<"put" | "post">>([]);
    const [signature, setSignature] = useState("");

    const { companyInfos, countryFeatures } = useCompanySettings();
    const companyId = companyInfos?.id;
    const hasVatOptions = countryFeatures.vat.options.length > 0;
    const usesUrssaf = countryFeatures.urssaf;
    const countryBankingFieldsKey = countryFeatures.bankingFields.join("|");

    const companyCountryCode = companyInfos?.countryCode ?? "";
    const siretField = getCreateCompanySiretField(companyCountryCode, t);

    const usesStock = !!+companySettings?.settings?.find((sttg: any) => sttg.name == "uses_stock")?.content;
    const paymentMethods = JSON.parse(
        companySettings?.settings?.find((sttg: any) => sttg.name == "accepted_payment_methods")?.content,
    );
    const usesPortal = JSON.parse(companySettings?.settings?.find((sttg: any) => sttg.name == "uses_portal")?.content);
    const socialProfileAllowed = JSON.parse(
        companySettings?.settings?.find((sttg: any) => sttg.name == "uses_portal_social_profile")?.content,
    );

    const blocker = useBlocker(isCompanyEdited || isBankEdited || isStockLocationsEdited);

    const handleCompanyInfoEdit = (data: Partial<ICompany>) => {
        setIsCompanyEdited(true);
        setCompanyData((prev) => ({ ...prev, ...data }));
    };

    const handleBankInfoEdit = (data: Partial<ICompany>) => {
        setIsBankEdited(true);
        setBankInfo((prev) => ({ ...prev, ...data }));
        setCompanyData((prev) => ({ ...prev, ...data }));
    };

    const handleAdditionalInfoEdit = (data: Partial<ICompany>) => {
        setIsCompanyEdited(true);
        setAdditionalInfo((prev) => ({ ...prev, ...data }));
        setCompanyData((prev) => ({ ...prev, ...data }));
    };

    const uploadCompanyAvatar = async (files: any) => {
        const sizeError = fileWeightValidator(files[0], 1000000);
        if (sizeError) {
            toast.error(`${t("Settings.FileError", { size: "1" })}`);
        } else {
            const response = await dispatch(sendPicture(files[0]));
            if (response.meta.requestStatus === "fulfilled") {
                dispatch(patchCompanyInfo({ avatarUrl: response.payload as string }));
            }
        }
    };

    const uploadCompanyInfo = () => {
        if (isCompanyEdited || isBankEdited) {
            if (
                validateCompanyInfoBeforeSubmit(
                    {
                        isCompanyEdited,
                        isBankEdited,
                        siret: companyData?.siret,
                        bankInfo: bankInfo as Record<string, string>,
                    },
                    countryFeatures,
                    t,
                )
            )
                return;

            const usesIbanBanking = countryFeatures.bankingFields.includes("iban");

            const metadataBankingFieldKeys = [
                ...countryFeatures.bankingFields.filter((fieldKey) => fieldKey !== "iban" && fieldKey !== "bic"),
                ...(countryFeatures.companyBankingFields ?? []),
            ];

            const bankingMetadata = metadataBankingFieldKeys.reduce(
                (acc, key) => {
                    const value = (bankInfo as Record<string, string | undefined>)[key];
                    if (value != null && value !== "") {
                        acc[key] = value;
                    }
                    return acc;
                },
                {} as Partial<Record<string, string>>,
            );

            const bankInfoPayload = usesIbanBanking
                ? {
                      bankAccountName: (bankInfo as Record<string, string | undefined>)?.bankAccountName,
                      iban: (bankInfo as Record<string, string | undefined>)?.iban,
                      swift: (bankInfo as Record<string, string | undefined>)?.swift,
                      bankingMetadata: undefined,
                  }
                : {
                      // Send bankingMetadata directly without merging with existing —
                      // so cleared fields (empty string) are truly removed.
                      bankingMetadata: Object.keys(bankingMetadata).length > 0 ? bankingMetadata : null,
                  };

            dispatch(
                patchCompanyInfo({
                    ...companyData,
                    ...bankInfoPayload,
                    signature,
                    id: companyId,
                }),
            ).then((response) => {
                if (response.meta.requestStatus === "fulfilled") {
                    dispatch(fetchCompanyInfo());
                    setIsCompanyEdited(false);
                    setIsBankEdited(false);
                    setIsStockLocationsEdited(false);
                    blocker.reset?.();
                }
            });
        }
        if (isStockLocationsEdited) {
            if (updateTypes.includes("post")) {
                dispatch(
                    postLocations(
                        locationList
                            .filter((item: any) => item.id == null && item.name != "" && item.name != null)
                            .map((item: any) => item.name),
                    ),
                );
            }
            if (updateTypes.includes("put")) {
                locationList
                    .filter((item: any) => item.id != null && item.id != undefined && item.modified)
                    .map((item: any) => {
                        dispatch(putLocations({ locationId: item.id, locationName: item.name }));
                    });
            }
        }
        if (isStockLocationsEdited && !isCompanyEdited && !isBankEdited) {
            setIsStockLocationsEdited(false);
            blocker.reset?.();
        }
    };

    const handleLocationUpdate = (e: any[], type: string) => {
        setUpdateTypes((types) => [...(new Set([...types, type]) as any)]);
        setLocationList(e);
    };

    const handleDefaultCompensation = (e: any) => {
        setCompanyData((compData) => ({
            ...compData,
            useCompensationByDefault: e.target.checked,
        }));
        setIsCompanyEdited(true);
    };

    const handleSignatureChange = (e: any) => {
        const sizeError = fileWeightValidator(e[0], 1000000);
        if (sizeError) {
            toast.error(`${t("Settings.FileError", { size: "1" })}`);
        } else {
            dispatch(sendCompanySignature(e[0])).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setSignature(res.payload as string);
                }
                if (res.meta.requestStatus === "rejected") {
                    setSignature("");
                }
            });
            setIsCompanyEdited(true);
        }
    };

    const handlePaymentsChange = (methods: string[]) => {
        const newSettings = companyData.settings.map((sttg) => {
            if (sttg.name === "accepted_payment_methods") {
                return { ...sttg, content: JSON.stringify(methods) };
            }
            return sttg;
        });
        setCompanyData((compData) => ({ ...compData, settings: newSettings }));
        setIsCompanyEdited(true);
    };

    const handleSocialProfile = (value: boolean) => {
        const newValue = value ? "1" : "0";
        const newSettings = companyData.settings.map((sttg) => {
            if (sttg.name === "uses_portal_social_profile") {
                return { ...sttg, content: newValue };
            }
            return sttg;
        });
        setCompanyData((compData) => ({ ...compData, settings: newSettings }));
        setIsCompanyEdited(true);
    };

    useEffect(() => {
        if (!companySettings) dispatch(fetchCompanyInfo());
    }, [companySettings, dispatch]);

    useEffect(() => {
        setCompanyData(companySettings);
    }, [companySettings]);

    useEffect(() => {
        const usesIban = countryFeatures.bankingFields.includes("iban");
        const rawMeta = (companyData as any)?.bankingMetadata;
        const metadataBankingInfo =
            typeof rawMeta === "string"
                ? (() => {
                      try {
                          return JSON.parse(rawMeta);
                      } catch {
                          return {};
                      }
                  })()
                : rawMeta ?? {};

        const companyOnlyFields = countryFeatures.companyBankingFields ?? [];
        const dynamicBankInfo = usesIban
            ? {
                  bankAccountName: companyData?.bankAccountName,
                  iban: companyData?.iban,
                  swift: companyData?.swift,
                  ...companyOnlyFields.reduce(
                      (acc, key) => {
                          acc[key] = metadataBankingInfo[key] ?? "";
                          return acc;
                      },
                      {} as Record<string, string>,
                  ),
              }
            : [
                  ...countryFeatures.bankingFields.filter((k) => k !== "iban" && k !== "bic"),
                  ...companyOnlyFields,
              ].reduce(
                  (acc, fieldKey) => {
                      acc[fieldKey] = (companyData as any)?.[fieldKey] ?? metadataBankingInfo[fieldKey] ?? "";
                      return acc;
                  },
                  {} as Record<string, string>,
              );

        setCompanyInfo({
            name: companyData?.name,
            siret: companyData?.siret,
            ...(hasVatOptions ? { intraCommunityVAT: companyData?.intraCommunityVAT } : {}),
        });
        setAddress({
            address: companyData?.address,
            postalCode: companyData?.postalCode,
            city: companyData?.city,
            country: companyData?.country,
        });
        setBankInfo(dynamicBankInfo);
        setAdditionalInfo({
            phoneNumber: companyData?.phoneNumber,
            apeCode: companyData?.apeCode,
            activityType: companyData?.activityType,
            ...(usesUrssaf ? { distributorType: companyData?.distributorType } : {}),
        });
        if (signature == "") {
            setSignature(companyData?.signature ?? "");
        }
    }, [companyData, hasVatOptions, usesUrssaf, countryBankingFieldsKey]);

    return (
        <div className="company-info">
            <BlockModal blocker={blocker} />
            <div className="company-info-header-bg"></div>
            <div className="company-info-main">
                <div className="company-info-main-header">
                    <div className="company-info-avatar">
                        <img src={companyData ? companyData.avatarUrl : ""} alt="" />
                        <label htmlFor="profilPicture">{t("Settings.UploadPicture")}</label>
                        <input
                            type="file"
                            accept="image/*"
                            id="profilPicture"
                            style={{ display: "none" }}
                            onChange={(ev) => uploadCompanyAvatar(ev.target.files)}
                        />
                    </div>
                </div>

                <br />
                <div className="company-info-blocks">
                    <InfoBlockProfil
                        title={t("Settings.CompanyInfo")}
                        data={companyInfo}
                        handleEdit={handleCompanyInfoEdit}
                        fieldLabels={{ siret: siretField.label }}
                        fieldHelperText={{ siret: siretField.description ?? undefined }}
                    />
                </div>
                <div className="company-info-blocks">
                    <InfoBlockProfil
                        title={t("BeneficiarInfo.Address")}
                        data={address}
                        handleEdit={handleCompanyInfoEdit}
                    />
                </div>
                <div className="company-info-blocks">
                    <InfoBlockProfil
                        title={t("BeneficiarInfo.BankInfo")}
                        data={bankInfo}
                        handleEdit={handleBankInfoEdit}
                    />
                </div>
                <div className="company-info-blocks">
                    <PaymentMethod
                        paymentMethods={paymentMethods}
                        handleChange={(methods: string[]) => handlePaymentsChange(methods)}
                    />
                </div>
                <div className="company-info-signature">
                    <InputLabel sx={{ ...labelStyles, width: "300px !important" }} htmlFor="photo">
                        {t("CompanySettings.CompanyStamp")}
                    </InputLabel>
                    <div className="upload-image-element">
                        <Upload value={signature} onChange={handleSignatureChange} />
                        {signature !== "" && (
                            <Button onClick={() => setSignature("")} sx={{ height: "fit-content", color: "black" }}>
                                <RefreshIcon sx={{ color: "black" }} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="company-info-blocks">
                    <InfoBlockProfil
                        title={t("CompanySettings.AdditionalInformations")}
                        data={additionalInfo}
                        handleEdit={handleAdditionalInfoEdit}
                    />
                </div>

                {usesStock && !["audiovisual", "press"].includes(props.portalType) && (
                    <div className="company-info-blocks">
                        <StockSettings
                            hasEdited={() => setIsStockLocationsEdited(true)}
                            onLocationUpdate={(e: any, type: string) => handleLocationUpdate(e, type)}
                        />
                    </div>
                )}
                {!["audiovisual", "press"].includes(props.portalType) && (
                    <div className="company-info-blocks">
                        <CompanyPreferenceSettings
                            hasEdited={() => setIsCompanyEdited(true)}
                            useCompensationByDefault={companyData?.useCompensationByDefault ?? 0}
                            onUseCompensationByDefault={(e) => handleDefaultCompensation(e)}
                        />
                    </div>
                )}
                {usesPortal == "1" && (
                    <div className="company-info-blocks">
                        <SocialProfile
                            allowsSocialProfile={socialProfileAllowed}
                            handleChange={(value: boolean) => handleSocialProfile(value)}
                        />
                    </div>
                )}
            </div>
            {(isCompanyEdited || isBankEdited || isStockLocationsEdited) && (
                <SaveButton onSave={uploadCompanyInfo} buttonText={t("Settings.SaveChanges")} />
            )}
        </div>
    );
};

export default CompanyInfo;
